import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { User, Mail, Phone, Briefcase, FileText, Download, ArrowLeft, Maximize, Minimize, FileCode, MapPin } from "lucide-react";
import { jsPDF } from "jspdf";

export default function CandidateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("admin_token");
        const response = await api.get(`/candidates/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCandidate(response.data.data);
      } catch (error) {
        console.error("Error fetching candidate details:", error);
        toast.error("Failed to load candidate details.");
      } finally {
        setLoading(false);
      }
    };
    fetchCandidateDetails();
  }, [id]);

  const calculateCompleteness = () => {
    if (!candidate) return 0;
    let score = 0;
    const requiredPersonalFields = ['name', 'email', 'phone', 'gender', 'dob', 'current_city', 'current_address', 'preferred_location'];
    const filledFields = requiredPersonalFields.filter(field => candidate[field] && candidate[field].toString().trim() !== '');
    score += Math.round((filledFields.length / requiredPersonalFields.length) * 40);

    if (candidate.experiences && candidate.experiences.length > 0) score += 20;
    if (candidate.educations && candidate.educations.length > 0) score += 20;
    if (candidate.resume || candidate.resume_url) score += 20;

    return Math.min(score, 100);
  };

  // 🌟 Clean PDF Generation (No Coins)
  const handleDownloadPDF = () => {
    try {
      toast.loading("Generating Resume PDF...", { id: "pdf-toast" });

      const doc = new jsPDF();
      let y = 20;

      const checkPageSpace = (neededSpace) => {
        if (y + neededSpace > 280) {
          doc.addPage();
          y = 20;
        }
      };

      // Header / Name
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42);
      doc.text(candidate.name || "Candidate Name", 105, y, { align: "center" });
      y += 6;

      // Contact Info
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      const contactText = `${candidate.email || ""} | ${candidate.phone || ""} | ${candidate.current_city || ""}`;
      doc.text(contactText, 105, y, { align: "center" });
      y += 8;

      // Divider Line
      doc.setLineWidth(0.4);
      doc.setDrawColor(203, 213, 225);
      doc.line(20, y, 190, y);
      y += 10;

      const addSectionHeading = (title) => {
        checkPageSpace(15);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(30, 58, 138);
        doc.text(title.toUpperCase(), 20, y);
        y += 4;
        doc.setLineWidth(0.2);
        doc.setDrawColor(203, 213, 225);
        doc.line(20, y, 190, y);
        y += 6;
      };

      // Career Objective
      if (candidate.career_objective) {
        addSectionHeading("Career Objective");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(51, 65, 85);
        const splitObj = doc.splitTextToSize(candidate.career_objective, 170);
        checkPageSpace(splitObj.length * 4.5);
        doc.text(splitObj, 20, y);
        y += splitObj.length * 4.5 + 6;
      }

      // Personal Details
      addSectionHeading("Personal Details");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(51, 65, 85);
      doc.text(`Applied Position: ${candidate.job ? candidate.job.title : "N/A"}`, 20, y);
      doc.text(`Gender: ${candidate.gender || "N/A"}   |   DOB: ${candidate.dob || "N/A"}`, 110, y);
      y += 5;
      doc.text(`Preferred Location: ${candidate.preferred_location || "N/A"}`, 20, y);
      y += 5;
      doc.text(`Address: ${candidate.current_address || "N/A"}`, 20, y);
      y += 8;

      // Professional Experience
      addSectionHeading("Professional Experience");
      if (candidate.experiences && candidate.experiences.length > 0) {
        candidate.experiences.forEach(exp => {
          checkPageSpace(20);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(15, 23, 42);
          doc.text(`${exp.job_title} at ${exp.company_name}`, 20, y);
          
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8.5);
          doc.setTextColor(100, 116, 139);
          doc.text(`${exp.start_date || ""} - ${exp.end_date || "Present"} (${exp.employment_type || ""})`, 190, y, { align: "right" });
          y += 5;

          if (exp.job_description) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(51, 65, 85);
            const splitDesc = doc.splitTextToSize(exp.job_description, 170);
            checkPageSpace(splitDesc.length * 4.5);
            doc.text(splitDesc, 20, y);
            y += splitDesc.length * 4.5 + 4;
          }
          y += 2;
        });
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.text("No work experience added.", 20, y);
        y += 8;
      }

      // Education
      y += 2;
      addSectionHeading("Education");
      if (candidate.educations && candidate.educations.length > 0) {
        candidate.educations.forEach(edu => {
          checkPageSpace(15);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(15, 23, 42);
          doc.text(`${edu.qualification || ""} - ${edu.specialization || ""}`, 20, y);
          
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(30, 58, 138);
          doc.text(`Score: ${edu.percentage_cgpa || "N/A"}`, 190, y, { align: "right" });
          y += 4;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(100, 116, 139);
          doc.text(`${edu.college_university || ""} (Passing Year: ${edu.passing_year || ""})`, 20, y);
          y += 7;
        });
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.text("No education records added.", 20, y);
      }

      const safeName = candidate.name ? candidate.name.replace(/\s+/g, '_') : "Candidate";
      doc.save(`${safeName}_Resume.pdf`);
      toast.success("Resume PDF downloaded successfully!", { id: "pdf-toast" });
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF resume.", { id: "pdf-toast" });
    }
  };

  // 🌟 Clean Word Document Generation (No Coins)
  const handleDownloadWord = () => {
    try {
      toast.loading("Generating Word resume...", { id: "word-toast" });

      let htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'><title>${candidate.name} Resume</title>
        <style>
          body { font-family: 'Calibri', Arial, sans-serif; color: #333; line-height: 1.6; padding: 25px; }
          h1 { font-size: 24px; color: #0f172a; margin-bottom: 2px; text-align: center; text-transform: uppercase; }
          .contact { font-size: 11px; color: #64748b; text-align: center; margin-bottom: 20px; border-bottom: 2px solid #cbd5e1; padding-bottom: 10px; }
          h2 { font-size: 12px; color: #1e3a8a; border-bottom: 1px solid #cbd5e1; padding-bottom: 3px; margin-top: 20px; text-transform: uppercase; letter-spacing: 0.5px; }
          p { font-size: 11.5px; margin: 4px 0; }
          .item-title { font-weight: bold; font-size: 11.5px; color: #0f172a; margin-top: 8px; }
          .item-sub { font-size: 10.5px; color: #64748b; font-style: italic; }
        </style>
        </head>
        <body>
          <h1>${candidate.name}</h1>
          <div class="contact">
            Email: ${candidate.email} &nbsp;|&nbsp; Phone: ${candidate.phone || "N/A"} &nbsp;|&nbsp; Location: ${candidate.current_city || "N/A"}
          </div>

          ${candidate.career_objective ? `
            <h2>Career Objective</h2>
            <p>${candidate.career_objective}</p>
          ` : ''}

          <h2>Personal Details</h2>
          <p><strong>Applied Position:</strong> ${candidate.job ? candidate.job.title : "N/A"}</p>
          <p><strong>Gender:</strong> ${candidate.gender || "-"} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Date of Birth:</strong> ${candidate.dob || "-"}</p>
          <p><strong>Preferred Location:</strong> ${candidate.preferred_location || "-"}</p>
          <p><strong>Address:</strong> ${candidate.current_address || "-"}</p>

          <h2>Professional Experience</h2>
          ${candidate.experiences && candidate.experiences.length > 0 ? candidate.experiences.map(exp => `
            <div style="margin-bottom: 12px;">
              <div class="item-title">${exp.job_title} — ${exp.company_name}</div>
              <div class="item-sub">${exp.start_date} to ${exp.end_date || "Present"} (${exp.employment_type})</div>
              <p>${exp.job_description || ""}</p>
            </div>
          `).join('') : '<p style="color: #94a3b8; font-style: italic;">No work experience added.</p>'}

          <h2>Education</h2>
          ${candidate.educations && candidate.educations.length > 0 ? candidate.educations.map(edu => `
            <div style="margin-bottom: 10px;">
              <div class="item-title">${edu.qualification} - ${edu.specialization}</div>
              <div class="item-sub">${edu.college_university} (Passing Year: ${edu.passing_year})</div>
              <p><strong>Score/CGPA:</strong> ${edu.percentage_cgpa}</p>
            </div>
          `).join('') : '<p style="color: #94a3b8; font-style: italic;">No education records added.</p>'}
        </body>
        </html>
      `;

      const blob = new Blob(['\ufeff' + htmlContent], {
        type: 'application/msword'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${candidate.name.replace(/\s+/g, '_')}_Resume.doc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Word resume downloaded successfully!", { id: "word-toast" });
    } catch (error) {
      console.error("Word generation error:", error);
      toast.error("Failed to generate Word document.", { id: "word-toast" });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!candidate) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Candidate not found.</p>
          <button onClick={() => navigate(-1)} className="mt-4 bg-yellow-500 text-black px-4 py-2 rounded-lg font-semibold cursor-pointer">
            Go Back
          </button>
        </div>
      </AdminLayout>
    );
  }

  const profileCompleteness = calculateCompleteness();
  const resumePath = candidate.resume || candidate.resume_url;
  const baseURL = api.defaults.baseURL;
  const fullUrl = candidate ? `${baseURL}/candidates/${candidate.id}/resume` : null;
  const extension = resumePath ? resumePath.split('.').pop().toLowerCase() : null;
  const canPreview = ['pdf', 'jpg', 'jpeg', 'png'].includes(extension);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-black font-semibold bg-white border border-gray-200 px-4 py-2 rounded-xl transition-colors shadow-sm w-fit cursor-pointer"
        >
          <ArrowLeft size={18} /> Back to Candidates
        </button>

        {/* Export Action Bar */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-1.5 text-xs font-bold text-gray-900 bg-yellow-400 hover:bg-yellow-500 px-4 py-2.5 rounded-xl transition-colors shadow-sm cursor-pointer"
          >
            <Download size={14} /> Download Info as PDF
          </button>
          <button
            onClick={handleDownloadWord}
            className="flex items-center gap-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2.5 rounded-xl transition-colors shadow-sm cursor-pointer"
          >
            <FileCode size={14} /> Download Info as Word
          </button>
        </div>
      </div>

      <PageTitle title="Candidate Profile Details" subtitle="Detailed information, work experience, education, and resume preview." />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-8">
        
        {/* Header banner with Wallet Balance & Completeness */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-yellow-500 text-black font-bold text-2xl rounded-2xl flex items-center justify-center shadow">
              {candidate.name ? candidate.name.charAt(0).toUpperCase() : "C"}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{candidate.name}</h2>
              <p className="text-sm text-gray-500">{candidate.email}</p>
              <p className="text-sm text-gray-500">{candidate.phone || "Phone not added"} • {candidate.current_city || "City not added"}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            {/* Wallet Balance Card for UI */}
            <div className="w-full sm:w-auto bg-gradient-to-br from-yellow-50 to-orange-50 p-3.5 rounded-xl border border-yellow-200 flex items-center gap-3 shadow-sm">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center text-xl shadow-inner border border-yellow-300">
                🪙
              </div>
              <div className="pr-2">
                <p className="text-[10px] text-yellow-700 font-extrabold uppercase tracking-wider">Wallet Balance</p>
                <h3 className="text-lg font-black text-gray-900 leading-tight">
                  {candidate.coins ? candidate.coins : 0} <span className="text-xs text-yellow-700 font-bold">Coins</span>
                </h3>
              </div>
            </div>

            {/* Profile Completeness Card */}
            <div className="w-full sm:w-64 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-600">Profile Completeness</span>
                <span className="font-bold text-gray-900">{profileCompleteness}%</span>
              </div>
              <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                <div className="bg-yellow-500 h-full rounded-full transition-all duration-500" style={{ width: `${profileCompleteness}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-md font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4 flex items-center gap-2">
            <User className="text-yellow-500" /> Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Full Name</span>
              <p className="font-semibold text-gray-800">{candidate.name}</p>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Email Address</span>
              <a href={`mailto:${candidate.email}`} className="font-semibold text-yellow-600 hover:underline">{candidate.email}</a>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Phone Number</span>
              <a href={`tel:${candidate.phone}`} className="font-semibold text-gray-800">{candidate.phone}</a>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Status</span>
              <span className="px-2.5 py-1 bg-blue-50 text-blue-600 rounded-md text-xs font-bold capitalize border border-blue-100">
                {candidate.status}
              </span>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Applied Job</span>
              <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                <Briefcase size={14} className="text-yellow-600"/> {candidate.job ? candidate.job.title : "N/A"}
              </p>
            </div>
            {/* Wallet Coins in UI Grid */}
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Wallet Coins</span>
              <p className="font-bold text-gray-900 flex items-center gap-1">
                {candidate.coins ? candidate.coins : 0} 🪙
              </p>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Gender</span>
              <p className="font-semibold text-gray-800">{candidate.gender || "-"}</p>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Date of Birth</span>
              <p className="font-semibold text-gray-800">{candidate.dob || "-"}</p>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Current City</span>
              <p className="font-semibold text-gray-800">{candidate.current_city || "-"}</p>
            </div>
            <div>
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Preferred Job Location</span>
              <p className="font-semibold text-gray-800">{candidate.preferred_location || "-"}</p>
            </div>
            <div className="sm:col-span-3">
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Current Address</span>
              <p className="font-semibold text-gray-800">{candidate.current_address || "-"}</p>
            </div>
            <div className="sm:col-span-3">
              <span className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Career Objective</span>
              <p className="font-semibold text-gray-800">{candidate.career_objective || "-"}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="text-yellow-500" /> Work Experience
          </h3>
          {candidate.experiences && candidate.experiences.length > 0 ? (
            <div className="space-y-4">
              {candidate.experiences.map((exp, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="font-bold text-gray-900">{exp.job_title} at {exp.company_name}</h4>
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-semibold">{exp.employment_type}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{exp.start_date} to {exp.end_date || "Present"}</p>
                  <p className="text-sm text-gray-700">{exp.job_description}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No work experience added.</p>
          )}
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-md font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="text-yellow-500" /> Education Details
          </h3>
          {candidate.educations && candidate.educations.length > 0 ? (
            <div className="space-y-4">
              {candidate.educations.map((edu, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-900">{edu.qualification} - {edu.specialization}</h4>
                    <p className="text-xs text-gray-500 mt-1">{edu.college_university} ({edu.passing_year})</p>
                  </div>
                  <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-lg font-bold">Score: {edu.percentage_cgpa}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No education records added.</p>
          )}
        </div>

      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mt-8 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-md font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-yellow-500" /> Uploaded Original Resume / CV
          </h3>
          
          <div className="flex items-center gap-2">
            {canPreview && (
              <button 
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 px-3.5 py-2 rounded-xl transition-colors cursor-pointer"
              >
                <Maximize size={14} /> Fullscreen
              </button>
            )}

            {resumePath && (
              <a 
                href={fullUrl} 
                download 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold text-black bg-yellow-500 hover:bg-yellow-600 px-4 py-2 rounded-xl transition-colors shadow-sm cursor-pointer"
              >
                <Download size={14} /> Download Original Resume
              </a>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden relative min-h-[400px] flex items-center justify-center">
          {(() => {
            if (!resumePath) {
              return (
                <div className="text-center text-gray-400 py-12">
                  <FileText size={48} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">No resume uploaded</p>
                </div>
              );
            }
            if (extension === 'pdf') {
              return <iframe src={fullUrl} className="w-full h-[600px]" title="Resume Preview" />;
            }
            if (['jpg', 'jpeg', 'png'].includes(extension)) {
              return <img src={fullUrl} alt="Resume Preview" className="max-w-full max-h-[600px] object-contain p-2" />;
            }
            return (
              <div className="text-center p-8">
                <div className="w-16 h-16 bg-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText size={32} />
                </div>
                <h4 className="text-gray-800 font-bold mb-1">Document File (.doc / .docx)</h4>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  Word documents cannot be previewed directly in the browser during local development.
                </p>
                <a href={fullUrl} download className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-xl font-medium transition-colors text-sm shadow-sm">
                  <Download size={16} /> Download to View
                </a>
              </div>
            );
          })()}
        </div>
      </div>

      {isFullscreen && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col backdrop-blur-sm">
          <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
            <div className="text-white">
              <h3 className="font-bold text-lg">{candidate.name} - Resume</h3>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href={fullUrl} 
                download 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
              >
                <Download size={16} /> Download
              </a>
              <button 
                onClick={() => setIsFullscreen(false)} 
                className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-bold shadow-lg cursor-pointer"
              >
                <Minimize size={16} /> Close Fullscreen
              </button>
            </div>
          </div>
          <div className="flex-1 w-full h-full overflow-hidden flex items-center justify-center p-4 sm:p-8">
            {extension === 'pdf' ? (
              <iframe src={fullUrl} className="w-full h-full max-w-5xl bg-white rounded-xl shadow-2xl" title="Resume Fullscreen" />
            ) : (
              <img src={fullUrl} alt="Resume Fullscreen" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}